import React, { useContext, useEffect, useReducer, useState } from "react";

import openSocket from "socket.io-client";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Box,
  Tabs,
  Tab,
  AppBar
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { SwitchCamera } from "@material-ui/icons";
import { DeleteOutline, Edit, Description, RecordVoiceOver, TextFields } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import AssistantModal from "../../components/AssistantModal";
import toastUtils from "../../utils/toastUtils";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
// import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // 0 = prompts, 1 = assistants
  const [assistantType, setAssistantType] = useState(""); // "voice", "text"

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: "", item: null });
  
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toastUtils.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.", 'Erro no Prompt');
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTab === 0) {
      fetchPrompts();
    } else if (currentTab === 1) {
      fetchAssistants();
    }
  }, [currentTab]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prompt");
      dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistants = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assistants");
      setAssistants(data.assistants || []);
    } catch (err) {
      toastError(err);
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // const socket = socketManager.GetSocket();

    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket]);

  const handleOpenPromptModal = () => {
    setCurrentTab(0);
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toastUtils.info(i18n.t(data.message), 'Prompt Removido');
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const handleDeleteAssistant = async (assistantId) => {
    try {
      await api.delete(`/assistants/${assistantId}`);
      toastUtils.success(i18n.t("talkAi.assistants.messages.deleted"), 'Prompt Removido');
      fetchAssistants();
    } catch (err) {
      toastError(err);
    }
    setSelectedAssistant(null);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget.type === "prompt") {
      handleDeletePrompt(deleteTarget.item.id);
    } else if (deleteTarget.type === "assistant") {
      handleDeleteAssistant(deleteTarget.item.id);
    }
    setConfirmModalOpen(false);
    setDeleteTarget({ type: "", item: null });
  };

  const openDeleteConfirmation = (type, item) => {
    setDeleteTarget({ type, item });
    setConfirmModalOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCreatePrompt = () => {
    setCurrentTab(0);
    setPromptModalOpen(true);
  };

  const handleCreateAssistant = (type = "text") => {
    setAssistantType(type);
    setCurrentTab(1);
    setAssistantModalOpen(true);
  };

  const handleEditAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setAssistantModalOpen(true);
  };

  const handleCloseAssistantModal = () => {
    setAssistantModalOpen(false);
    setSelectedAssistant(null);
    fetchAssistants();
  };

  const renderMainView = () => (
    <>
      <MainHeader>
        <Title>{i18n.t("talkAi.title")}</Title>
        <MainHeaderButtonsWrapper>
          {currentTab === 0 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreatePrompt}
            >
              {i18n.t("prompts.buttons.add")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleCreateAssistant("text")}
            >
              {i18n.t("talkAi.assistants.addButton")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tabsContainer}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={i18n.t("prompts.title")} />
            <Tab label={i18n.t("talkAi.assistants.title")} />
          </Tabs>
        </div>
        
        <div className={classes.tabPanel}>
          {currentTab === 0 ? renderPromptsTable() : renderAssistantsTable()}
        </div>
      </Paper>
    </>
  );


  const renderPromptsTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell align="left">
            {i18n.t("prompts.table.name")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("prompts.table.queue")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("prompts.table.max_tokens")}
          </TableCell>
          <TableCell align="center">
            {i18n.t("prompts.table.actions")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <>
          {prompts.map((prompt) => (
            <TableRow key={prompt.id}>
              <TableCell align="left">{prompt.name}</TableCell>
              <TableCell align="left">{prompt.queue?.name}</TableCell>
              <TableCell align="left">{prompt.maxTokens}</TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={() => handleEditPrompt(prompt)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => openDeleteConfirmation("prompt", prompt)}
                >
                  <DeleteOutline />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {loading && <TableRowSkeleton columns={4} />}
        </>
      </TableBody>
    </Table>
  );

  const renderAssistantsTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell align="left">
            {i18n.t("talkAi.assistants.table.name")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("talkAi.assistants.table.type")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("talkAi.assistants.table.queue")}
          </TableCell>
          <TableCell align="left">
            {i18n.t("talkAi.assistants.table.status")}
          </TableCell>
          <TableCell align="center">
            {i18n.t("talkAi.assistants.table.actions")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <>
          {assistants.map((assistant) => (
            <TableRow key={assistant.id}>
              <TableCell align="left">{assistant.name}</TableCell>
              <TableCell align="left">
                {assistant.agentMode === "prompt" ? 
                  i18n.t("talkAi.assistants.agentModes.prompt") : 
                  assistant.agentMode === "agent_direct" ?
                  i18n.t("talkAi.assistants.agentModes.agentDirect") :
                  assistant.agentMode === "agent_assistant" ?
                  i18n.t("talkAi.assistants.agentModes.agentAssistant") :
                  "Não definido"
                }
              </TableCell>
              <TableCell align="left">{assistant.queue?.name}</TableCell>
              <TableCell align="left">
                {assistant.isActive ? 
                  i18n.t("talkAi.assistants.table.active") : 
                  i18n.t("talkAi.assistants.table.inactive")
                }
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={() => handleEditAssistant(assistant)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => openDeleteConfirmation("assistant", assistant)}
                >
                  <DeleteOutline />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {loading && <TableRowSkeleton columns={5} />}
        </>
      </TableBody>
    </Table>
  );

  // Remover views antigas e usar apenas renderMainView

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deleteTarget.item &&
          `${i18n.t(deleteTarget.type === "prompt" ? "prompts.confirmationModal.deleteTitle" : "talkAi.assistants.deleteDialog.title")} ${deleteTarget.item.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t(deleteTarget.type === "prompt" ? "prompts.confirmationModal.deleteMessage" : "talkAi.assistants.deleteDialog.message")}
      </ConfirmationModal>
      
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      
      {assistantModalOpen && (
        <AssistantModal
          open={assistantModalOpen}
          onClose={handleCloseAssistantModal}
          assistantId={selectedAssistant?.id}
          assistantType={assistantType}
        />
      )}
      
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        renderMainView()
      )}
    </MainContainer>
  );
};

export default Prompts;
